import React, { useMemo, useState } from "react";
import { Button, Menu, Select } from "@mantine/core";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconFilter,
  IconSearch,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  WelcomeSurveyFilters,
  filterSelectStyles,
} from "./WelcomeSurveySummary";
import "./WelcomeSurveyCustomerSubscriptions.css";

// Survey states a customer row can be in, in the order the filter menu lists
// them.
const STATUS_FILTERS = [
  { id: "all", label: "All subscriptions" },
  { id: "not_sent", label: "Not sent" },
  { id: "awaiting", label: "Awaiting" },
  { id: "complete", label: "Complete" },
  { id: "flagged", label: "Flagged" },
];

// Whether the customer is still signed up or has cancelled. Every row already
// carries this as `state`; it narrows the table only, so the summary figures
// above keep covering the whole window — filtering the report itself down to
// cancellations would make "retention" and ROR read as 100%.
const CUSTOMER_STATE_FILTERS = [
  { value: "all", label: "All customers" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "cancelled", label: "Cancelled" },
];

// How each state reads in the table's first column.
const CUSTOMER_STATE_LABELS = {
  active: "Active",
  pending: "Pending",
  cancelled: "Cancelled",
};

const STATUS_LABELS = {
  not_sent: "Not sent",
  awaiting: "Awaiting",
  complete: "Complete",
};

function formatTimestamp(value) {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("M/D h:mm A") : "";
}

// How long the customer spent on the survey, as "1m 04s" / "58s". Sits under
// the device chip: a plausible span backs the chip up, a two-second one is the
// thing worth looking at. Anything that isn't a positive span - missing,
// unparseable, or a zero the clock can't really have measured - comes back
// empty so the caller shows nothing at all.
function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return "";
  const minutes = Math.floor(total / 60);
  const rest = Math.round(total % 60);
  return minutes ? `${minutes}m ${String(rest).padStart(2, "0")}s` : `${rest}s`;
}

// Which of the three device states a completed survey is in. "No device
// recorded" is neither clean nor flagged — it reads as unknown rather than
// borrowing the green a clean row gets.
function qualityVariant(quality, flagged) {
  if (flagged) return "flagged";
  return quality?.customers_on_device ? "clean" : "unknown";
}

// Sale date, as the small line under the subscription it belongs to.
function formatSaleDate(value) {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("MMM D, YYYY") : "";
}

// What a subscription is called. FieldRoutes doesn't always give one a service
// type, so the id it always has stands in — prefixed, so a bare number never
// reads as a name.
function subscriptionLabel(subscription) {
  if (!subscription) return "—";
  return subscription.name ?? `#${subscription.id}`;
}

// The response carries one row per subscription and the table shows them the
// same way: a customer who bought twice appears twice, each row carrying its
// own subscription, sale date and survey state. Nothing is merged, so no row
// stands for anything but the one sale it names.
function toSubscriptionRows(rows) {
  return (rows ?? []).map(row => ({
    // The FieldRoutes subscription id is unique per row, so it is the key.
    key: row.fr_subscription_id,
    customerId: row.customer_id ?? null,
    customerName: row.customer_name,
    repName: row.rep_name,
    officeName: row.office_name,
    saleDate: row.sale_date ?? null,
    subscription: {
      id: row.fr_subscription_id,
      name: row.service_type ?? null,
    },
    customerState: row.state ?? "active",
    surveySentAt: row.survey_sent_at ?? null,
    surveyCompletedAt: row.survey_completed_at ?? null,
    quality: row.quality ?? null,
    secondsToComplete: row.seconds_to_complete ?? null,
    completedSurveyId: row.survey_completed
      ? (row.welcome_survey_id ?? null)
      : null,
    sentSurveyId:
      row.survey_sent && !row.survey_completed
        ? (row.welcome_survey_id ?? null)
        : null,
    unsentSubscriptionId:
      !row.survey_sent && !row.survey_completed ? row.fr_subscription_id : null,
    status: row.survey_completed
      ? "complete"
      : row.survey_sent
        ? "awaiting"
        : "not_sent",
    // Set when the survey was submitted from a device that also answered for
    // another customer — what the "Flagged" filter tab selects on.
    flagged: Boolean(row.quality?.flagged),
  }));
}

/**
 * Row action, following the survey's state: a completed survey opens its
 * document, one that went out but came back empty can be sent again, and a
 * subscription that never had a survey can have one opened.
 */
function ViewDetailsButton({
  customer,
  onViewDocument,
  viewingDocumentId,
  onResendSurvey,
  resendingSurveyId,
  onCreateSurvey,
  creatingSubscriptionId,
}) {
  if (customer.status === "complete" && customer.completedSurveyId !== null) {
    const isLoading = viewingDocumentId === customer.completedSurveyId;
    return (
      <button
        type="button"
        className="ws-cs-details"
        disabled={isLoading}
        onClick={() => onViewDocument(customer.completedSurveyId)}
      >
        {isLoading ? "Opening…" : "View details"}
        <IconChevronRight size={13} />
      </button>
    );
  }

  if (customer.status === "awaiting" && customer.sentSurveyId !== null) {
    const isLoading = resendingSurveyId === customer.sentSurveyId;
    return (
      <button
        type="button"
        className="ws-cs-details"
        disabled={isLoading}
        onClick={() => onResendSurvey(customer.sentSurveyId)}
      >
        {isLoading ? "Sending…" : "Resend survey"}
        <IconChevronRight size={13} />
      </button>
    );
  }

  if (
    customer.status === "not_sent" &&
    customer.unsentSubscriptionId !== null
  ) {
    const isLoading = creatingSubscriptionId === customer.unsentSubscriptionId;
    return (
      <button
        type="button"
        className="ws-cs-details"
        disabled={isLoading}
        onClick={() => onCreateSurvey(customer.unsentSubscriptionId)}
      >
        {isLoading ? "Creating…" : "Create survey"}
        <IconChevronRight size={13} />
      </button>
    );
  }

  return null;
}

/**
 * The customer-level view of the reporting window: one row per customer with
 * their subscriptions, survey state and completion timing. Fed by
 * `customer_subscriptions` from the report-data response.
 */
function WelcomeSurveyCustomerSubscriptions({
  rows = [],
  loading,
  filters,
  onFiltersChange,
  offices = [],
  onViewDocument,
  viewingDocumentId,
  onResendSurvey,
  resendingSurveyId,
  onCreateSurvey,
  creatingSubscriptionId,
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customerState, setCustomerState] = useState("all");

  const subscriptionRows = useMemo(() => toSubscriptionRows(rows), [rows]);

  // Rows are subscriptions, so the number of customers behind them is its own
  // figure — two sales to one customer are two rows but one customer.
  const customerCount = useMemo(
    () =>
      new Set(
        subscriptionRows.map(
          row => row.customerId ?? `name:${row.customerName ?? row.key}`
        )
      ).size,
    [subscriptionRows]
  );

  const counts = useMemo(
    () => ({
      all: subscriptionRows.length,
      not_sent: subscriptionRows.filter(c => c.status === "not_sent").length,
      awaiting: subscriptionRows.filter(c => c.status === "awaiting").length,
      complete: subscriptionRows.filter(c => c.status === "complete").length,
      flagged: subscriptionRows.filter(c => c.flagged).length,
    }),
    [subscriptionRows]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    return subscriptionRows
      .filter(customer =>
        customerState === "all"
          ? true
          : customer.customerState === customerState
      )
      .filter(customer =>
        status === "all"
          ? true
          : status === "flagged"
            ? customer.flagged
            : customer.status === status
      )
      .filter(customer => {
        if (!term) return true;
        const haystack = [
          customer.customerName,
          customer.customerId,
          customer.repName,
          customer.officeName,
          customer.subscription.id,
          customer.subscription.name,
        ]
          .filter(value => value !== null && value !== undefined)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => {
        // Newest sale first, the order the endpoint returns rows in. Undated
        // sales sort last.
        const aTime = a.saleDate;
        const bTime = b.saleDate;
        if (aTime && bTime)
          return dayjs(bTime).valueOf() - dayjs(aTime).valueOf();
        if (aTime) return -1;
        if (bTime) return 1;
        return (a.customerName ?? "").localeCompare(b.customerName ?? "");
      });
  }, [subscriptionRows, search, status, customerState]);

  const filtersActive =
    status !== "all" || customerState !== "all" || search.trim() !== "";

  const activeStatus =
    STATUS_FILTERS.find(filter => filter.id === status) ?? STATUS_FILTERS[0];

  return (
    <section className="ws-cs">
      <div className="ws-cs-header">
        <h2 className="ws-summary-heading">Customer Subscriptions</h2>

        {/* What the filters below produced: the size of the result and the
            window it covers, opposite the title. */}
        <div className="ws-summary-label ws-cs-meta">
          {counts.all} {counts.all === 1 ? "subscription" : "subscriptions"}
          {` · ${customerCount} ${customerCount === 1 ? "customer" : "customers"}`}
        </div>
      </div>

      <div className="ws-cs-controls">
        <label className="ws-cs-search">
          <IconSearch size={15} />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search customers — name, code, rep, subscription"
            aria-label="Search customers"
          />
        </label>

        <div className="ws-cs-filters">
          <Menu shadow="md" radius="md" position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="default"
                radius="xl"
                leftSection={<IconFilter size={15} />}
                rightSection={<IconChevronDown size={14} />}
                className="ws-cs-filter-button"
              >
                {activeStatus.label}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Survey status</Menu.Label>
              {STATUS_FILTERS.map(filter => (
                <Menu.Item
                  key={filter.id}
                  onClick={() => setStatus(filter.id)}
                  leftSection={
                    status === filter.id ? (
                      <IconCheck size={14} />
                    ) : (
                      <span className="ws-cs-filter-tick" />
                    )
                  }
                  rightSection={
                    <span className="ws-cs-filter-count">
                      {counts[filter.id]}
                    </span>
                  }
                >
                  {filter.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          <Select
            data={CUSTOMER_STATE_FILTERS}
            value={customerState}
            onChange={value => setCustomerState(value ?? "all")}
            allowDeselect={false}
            radius="xl"
            aria-label="Filter by customer state"
            styles={filterSelectStyles}
          />

          {onFiltersChange && (
            <WelcomeSurveyFilters
              filters={filters}
              onChange={onFiltersChange}
              offices={offices}
            />
          )}
        </div>
      </div>

      <div className="ws-cs-table-scroll">
        <table className="ws-cs-table">
          <thead>
            <tr>
              <th>State</th>
              <th>Customer</th>
              <th>Sold by</th>
              <th>Subscription</th>
              <th>Status</th>
              <th>Quality</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(customer => (
              <tr key={customer.key}>
                <td>
                  <span
                    className={`ws-cs-dot ws-cs-dot-${customer.customerState}`}
                  />
                  <span className="ws-cs-state">
                    {CUSTOMER_STATE_LABELS[customer.customerState] ?? "Active"}
                  </span>
                </td>
                <td>
                  <div className="ws-cs-primary">
                    {customer.customerName || "Unsynced customer"}
                  </div>
                  {customer.customerId != null && (
                    <div className="ws-cs-secondary">
                      #{customer.customerId}
                    </div>
                  )}
                </td>
                <td>
                  <div className="ws-cs-primary ws-cs-primary-light">
                    {customer.repName || "Unattributed"}
                  </div>
                  <div className="ws-cs-caps">
                    {customer.officeName || "No office"}
                  </div>
                </td>
                <td>
                  <div className="ws-cs-primary ws-cs-primary-light ws-cs-subscription">
                    {subscriptionLabel(customer.subscription)}
                  </div>
                  {formatSaleDate(customer.saleDate) && (
                    <div className="ws-cs-secondary">
                      Sold {formatSaleDate(customer.saleDate)}
                    </div>
                  )}
                </td>
                <td>
                  <span
                    className={`ws-cs-status ws-cs-status-${customer.status.replace("_", "-")}`}
                  >
                    <span className="ws-cs-dot" />
                    {STATUS_LABELS[customer.status]}
                  </span>
                  <div className="ws-cs-secondary">
                    {formatTimestamp(
                      customer.surveyCompletedAt ?? customer.surveySentAt
                    )}
                  </div>
                </td>
                <td>
                  {customer.quality?.label ? (
                    <span
                      className={`ws-cs-quality ws-cs-quality-${qualityVariant(
                        customer.quality,
                        customer.flagged
                      )}`}
                      title={
                        customer.quality.customers_on_device > 1
                          ? `Submitted from a device that answered for ${customer.quality.customers_on_device} customers`
                          : undefined
                      }
                    >
                      <span className="ws-cs-dot" />
                      {customer.quality.label}
                    </span>
                  ) : (
                    // No completed survey yet, so nothing to judge.
                    <span className="ws-cs-secondary">—</span>
                  )}
                  {formatDuration(customer.secondsToComplete) && (
                    <div className="ws-cs-secondary">
                      {formatDuration(customer.secondsToComplete)} to complete
                    </div>
                  )}
                </td>
                <td>
                  <ViewDetailsButton
                    customer={customer}
                    onViewDocument={onViewDocument}
                    viewingDocumentId={viewingDocumentId}
                    onResendSurvey={onResendSurvey}
                    resendingSurveyId={resendingSurveyId}
                    onCreateSurvey={onCreateSurvey}
                    creatingSubscriptionId={creatingSubscriptionId}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!visible.length && (
          <div className="ws-cs-empty">
            {loading
              ? "Loading customers…"
              : filtersActive
                ? "No customers match these filters."
                : "No customer subscriptions in this window."}
          </div>
        )}
      </div>
    </section>
  );
}

export default WelcomeSurveyCustomerSubscriptions;
